// A radix-2 complex FFT, in place over split re/im arrays — the one
// transform the stretch engine needs. Twiddles and the bit-reversal table
// are built once per size and reused, so a render pays for them once.
// Pure: no DOM, runs the same in a Worker and under vitest.

export interface Fft {
  readonly size: number;
  /** Forward transform of `re`/`im`, in place. */
  forward(re: Float64Array, im: Float64Array): void;
  /** Inverse transform, in place, scaled by 1/size. */
  inverse(re: Float64Array, im: Float64Array): void;
}

const cache = new Map<number, Fft>();

/** The transform for `size` (a power of two), built on first use. */
export function fftOf(size: number): Fft {
  const hit = cache.get(size);
  if (hit) return hit;
  if (size < 2 || (size & (size - 1)) !== 0) throw new Error(`fft size ${size} is not a power of two`);
  const bits = Math.log2(size);
  const rev = new Uint32Array(size);
  for (let i = 0; i < size; i++) {
    let r = 0;
    for (let b = 0; b < bits; b++) r |= ((i >> b) & 1) << (bits - 1 - b);
    rev[i] = r;
  }
  const cos = new Float64Array(size >> 1);
  const sin = new Float64Array(size >> 1);
  for (let i = 0; i < size >> 1; i++) {
    cos[i] = Math.cos((2 * Math.PI * i) / size);
    sin[i] = Math.sin((2 * Math.PI * i) / size);
  }

  const run = (re: Float64Array, im: Float64Array, sign: number): void => {
    for (let i = 0; i < size; i++) {
      const j = rev[i]!;
      if (j > i) {
        const tr = re[i]!;
        re[i] = re[j]!;
        re[j] = tr;
        const ti = im[i]!;
        im[i] = im[j]!;
        im[j] = ti;
      }
    }
    for (let len = 2; len <= size; len <<= 1) {
      const half = len >> 1;
      const step = size / len;
      for (let start = 0; start < size; start += len) {
        for (let k = 0; k < half; k++) {
          const wr = cos[k * step]!;
          const wi = sign * sin[k * step]!;
          const a = start + k;
          const b = a + half;
          const xr = re[b]! * wr - im[b]! * wi;
          const xi = re[b]! * wi + im[b]! * wr;
          re[b] = re[a]! - xr;
          im[b] = im[a]! - xi;
          re[a] = re[a]! + xr;
          im[a] = im[a]! + xi;
        }
      }
    }
  };

  const fft: Fft = {
    size,
    forward: (re, im) => run(re, im, -1),
    inverse: (re, im) => {
      run(re, im, 1);
      const scale = 1 / size;
      for (let i = 0; i < size; i++) {
        re[i] = re[i]! * scale;
        im[i] = im[i]! * scale;
      }
    },
  };
  cache.set(size, fft);
  return fft;
}

/** A periodic Hann window of `n` points. */
export function hannWindow(n: number): Float64Array {
  const w = new Float64Array(n);
  for (let i = 0; i < n; i++) w[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / n);
  return w;
}
